import type { ReactNode } from "react";

export type UnifiedTableRow = Record<string, unknown>;

export type UnifiedTableDownloadFormat =
  | "csv"
  | "pdf"
  | "xls"
  | "xlsx"
  | string;

export interface UnifiedTableDownloadLinks {
  csv?: string;
  pdf?: string;
  xls?: string;
  xlsx?: string;
  [key: string]: string | undefined;
}

export interface UnifiedTableUI {
  searchable?: boolean;
  searchPlaceholder?: string;
  searchKeys?: string[];
  downloadable?: UnifiedTableDownloadFormat[];
  showTableDownloads?: boolean;
  downloadLinks?: UnifiedTableDownloadLinks;
  defaultRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  emptyMessage?: string;
  emptyStateIconRendererId?: string;
  emptyStateVariant?: "table" | "panel";
  showPaginator?: boolean;
  rowHover?: boolean;
  stripedRows?: boolean;
}

export interface UnifiedTableColumn {
  key: string;
  header: string;
  sortable?: boolean;
  width?: number | string;
  align?: "left" | "center" | "right";
  frozen?: "left" | "right";
  rendererId?: string;
}

export interface UnifiedTableRowAction {
  id: string;
  label: string;
  icon?: ReactNode;
  /* eslint-disable no-unused-vars */
  showWhen?(row: UnifiedTableRow): boolean;
  disabledWhen?(row: UnifiedTableRow): boolean;
  /* eslint-enable no-unused-vars */
}

export interface UnifiedTableBlock {
  id: string;
  columns: UnifiedTableColumn[];
  rows: UnifiedTableRow[];
  ui?: UnifiedTableUI;
  actions?: UnifiedTableRowAction[];
  meta?: Record<string, unknown>;
}

export interface UnifiedTableSection {
  id: string;
  title: string;
  tables: UnifiedTableBlock[];
  description?: string;
}

export interface UnifiedTableSubnavItem {
  sectionId: string;
  label: string;
}

export interface UnifiedTableRecord {
  patientId?: string;
  module?: string;
  pageTitle?: string;
  subnav?: UnifiedTableSubnavItem[];
  sections: UnifiedTableSection[];
}

export interface UnifiedDataTableCellContext {
  value: unknown;
  row: UnifiedTableRow;
  column: UnifiedTableColumn;
  table: UnifiedTableBlock;
  section: UnifiedTableSection;
  record: UnifiedTableRecord;
}

/* eslint-disable no-unused-vars */
export interface UnifiedDataTableColumnRenderer {
  (context: UnifiedDataTableCellContext): ReactNode;
}
/* eslint-enable no-unused-vars */

export interface UnifiedDataTableColumnRenderers {
  [key: string]: UnifiedDataTableColumnRenderer;
}

export interface UnifiedDataTableEmptyStateIconRenderers {
  [key: string]: ReactNode;
}

export interface UnifiedDataTableRowContext {
  row: UnifiedTableRow;
  rowIndex: number;
  table: UnifiedTableBlock;
  section: UnifiedTableSection;
  record: UnifiedTableRecord;
}

/* eslint-disable no-unused-vars */
export interface UnifiedDataTableRowActionHandler {
  (row: UnifiedTableRow): void;
}
/* eslint-enable no-unused-vars */

export interface UnifiedDataTableRowActionHandlers {
  view?: UnifiedDataTableRowActionHandler;
  download?: UnifiedDataTableRowActionHandler;
  duplicate?: UnifiedDataTableRowActionHandler;
  cancel?: UnifiedDataTableRowActionHandler;
  edit?: UnifiedDataTableRowActionHandler;
  [actionId: string]: UnifiedDataTableRowActionHandler | undefined;
}

export const DEFAULT_TABLE_ROWS_PER_PAGE = 10;
export const DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 25] as const;

// Temporary aliases for incremental migration from existing `I*` names.
export type IProblemRecord = UnifiedTableRecord;
export type ISubnavItem = UnifiedTableSubnavItem;
export type ISection = UnifiedTableSection;
export type ITableBlock = UnifiedTableBlock;
export type ITableColumn = UnifiedTableColumn;
export type ITableUI = UnifiedTableUI;
